import { useEffect, useMemo, useState } from "react";
import { VehicleCard } from "@/components/site/VehicleCard";
import { VehicleModal } from "@/components/site/VehicleModal";
import { Skeleton } from "@/components/ui/skeleton";
import { useVehicles } from "@/application/hooks/use-vehicles";
import { uniqueSorted, type Vehicle } from "@/domain/models/vehicle";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useI18n } from "@/i18n";

type SortKey =
  | "featured"
  | "price-asc"
  | "price-desc"
  | "year-desc"
  | "mileage-asc"
  | "power-desc";

const DEFAULT_MAX_PRICE = 200_000;

export function CatalogPage() {
  const { t, tf } = useI18n();
  usePageMeta({
    title: t.meta.catalogTitle,
    description: t.meta.catalogDescription,
  });

  const { status, data: vehicles, error, reload } = useVehicles();

  const [q, setQ] = useState("");
  const [brand, setBrand] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [fuel, setFuel] = useState<string>("");
  const [transmission, setTransmission] = useState<string>("");
  const [condition, setCondition] = useState<string>("");
  const [priceMax, setPriceMax] = useState<number>(DEFAULT_MAX_PRICE);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [sort, setSort] = useState<SortKey>("featured");
  const [showFilters, setShowFilters] = useState(false);

  const [active, setActive] = useState<Vehicle | null>(null);
  const [open, setOpen] = useState(false);

  const maxPrice = useMemo(() => {
    if (!vehicles?.length) return DEFAULT_MAX_PRICE;
    return Math.max(...vehicles.map((v) => v.price));
  }, [vehicles]);

  useEffect(() => {
    if (status === "success") {
      setPriceMax(maxPrice);
    }
  }, [status, maxPrice]);

  const brands = useMemo(
    () => (vehicles ? uniqueSorted(vehicles.map((v) => v.brand)) : []),
    [vehicles],
  );
  const bodies = useMemo(
    () => (vehicles ? uniqueSorted(vehicles.map((v) => v.body)) : []),
    [vehicles],
  );
  const fuels = useMemo(
    () => (vehicles ? uniqueSorted(vehicles.map((v) => v.fuel)) : []),
    [vehicles],
  );
  const transmissions = useMemo(
    () => (vehicles ? uniqueSorted(vehicles.map((v) => v.transmission)) : []),
    [vehicles],
  );
  const conditions = useMemo(
    () => (vehicles ? uniqueSorted(vehicles.map((v) => v.condition)) : []),
    [vehicles],
  );

  const results = useMemo(() => {
    if (!vehicles) return [];

    let list = vehicles.filter((v) => {
      if (q && !`${v.brand} ${v.model}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (brand && v.brand !== brand) return false;
      if (body && v.body !== body) return false;
      if (fuel && v.fuel !== fuel) return false;
      if (transmission && v.transmission !== transmission) return false;
      if (condition && v.condition !== condition) return false;
      if (v.price > priceMax) return false;
      if (onlyAvailable && !v.available) return false;
      return true;
    });
    switch (sort) {
      case "price-asc":
        list = list.slice().sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list = list.slice().sort((a, b) => b.price - a.price);
        break;
      case "year-desc":
        list = list.slice().sort((a, b) => b.year - a.year);
        break;
      case "mileage-asc":
        list = list.slice().sort((a, b) => a.mileage - b.mileage);
        break;
      case "power-desc":
        list = list.slice().sort((a, b) => b.power - a.power);
        break;
    }
    return list;
  }, [vehicles, q, brand, body, fuel, transmission, condition, priceMax, onlyAvailable, sort]);

  const clear = () => {
    setQ("");
    setBrand("");
    setBody("");
    setFuel("");
    setTransmission("");
    setCondition("");
    setPriceMax(maxPrice);
    setOnlyAvailable(false);
  };

  const sortEntries = Object.entries(t.catalog.sorts) as [SortKey, string][];

  return (
    <>
      <section className="relative overflow-hidden pb-8 pt-32 sm:pt-40">
        <div className="absolute inset-x-0 top-0 -z-10 h-[60vh] bg-gradient-to-b from-primary/[0.06] via-transparent to-transparent" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.catalog.eyebrow}
          </div>
          <h1 className="mt-3 font-display text-5xl font-bold tracking-tight sm:text-6xl">
            {t.catalog.title}
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            {status === "success"
              ? tf(t.catalog.subtitleLoaded, { count: vehicles.length })
              : t.catalog.subtitleIdle}
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[280px_1fr] lg:px-8">
          <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
            <div className="sticky top-24 rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-sm font-semibold uppercase tracking-wider">
                  {t.catalog.filters}
                </h2>
                <button
                  onClick={clear}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {t.catalog.reset}
                </button>
              </div>

              <div className="mt-5 space-y-5">
                <SearchInput
                  value={q}
                  onChange={setQ}
                  placeholder={t.catalog.searchPlaceholder}
                />
                <SelectRow
                  label={t.catalog.brand}
                  value={brand}
                  setValue={setBrand}
                  options={brands}
                  allLabel={t.catalog.all}
                />
                <SelectRow
                  label={t.catalog.bodyStyle}
                  value={body}
                  setValue={setBody}
                  options={bodies}
                  allLabel={t.catalog.all}
                />
                <SelectRow
                  label={t.catalog.fuel}
                  value={fuel}
                  setValue={setFuel}
                  options={fuels}
                  allLabel={t.catalog.all}
                />
                <SelectRow
                  label={t.catalog.transmission}
                  value={transmission}
                  setValue={setTransmission}
                  options={transmissions}
                  allLabel={t.catalog.all}
                />
                <SelectRow
                  label={t.catalog.condition}
                  value={condition}
                  setValue={setCondition}
                  options={conditions}
                  allLabel={t.catalog.all}
                />

                <div>
                  <div className="flex items-baseline justify-between">
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {t.catalog.maxPrice}
                    </label>
                    <span className="font-display text-sm font-semibold">
                      ${priceMax.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={40000}
                    max={maxPrice}
                    step={1000}
                    value={priceMax}
                    onChange={(e) => setPriceMax(+e.target.value)}
                    className="mt-2 w-full"
                    style={{ accentColor: "var(--color-primary)" }}
                    disabled={status !== "success"}
                  />
                </div>

                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface p-3">
                  <input
                    type="checkbox"
                    checked={onlyAvailable}
                    onChange={(e) => setOnlyAvailable(e.target.checked)}
                    className="h-4 w-4"
                    style={{ accentColor: "var(--color-primary)" }}
                  />
                  <span className="text-sm">{t.catalog.inStockOnly}</span>
                </label>
              </div>
            </div>
          </aside>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters((s) => !s)}
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-border px-4 text-xs font-semibold lg:hidden"
                >
                  {showFilters ? (
                    <X className="h-3.5 w-3.5" />
                  ) : (
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                  )}
                  {showFilters ? t.catalog.hideFilters : t.catalog.showFilters}
                </button>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {status === "loading"
                    ? t.catalog.loading
                    : status === "error"
                      ? t.catalog.error
                      : tf(t.catalog.results, { count: results.length })}
                </span>
              </div>
              <label className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t.catalog.sort}
                </span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
                  disabled={status !== "success"}
                >
                  {sortEntries.map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {status === "loading" || status === "idle" ? (
              <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-80 rounded-2xl" />
                ))}
              </div>
            ) : null}

            {status === "error" ? (
              <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-16 text-center">
                <p className="font-display text-lg font-semibold">{t.catalog.loadError}</p>
                <p className="mt-2 text-sm text-muted-foreground">{error?.message}</p>
                <button
                  onClick={() => void reload()}
                  className="mt-6 inline-flex h-10 items-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground"
                >
                  {t.catalog.retry}
                </button>
              </div>
            ) : null}

            {status === "empty" ? (
              <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-16 text-center">
                <p className="font-display text-lg font-semibold">{t.catalog.emptyInventory}</p>
                <p className="mt-2 text-sm text-muted-foreground">{t.catalog.emptyInventoryHint}</p>
              </div>
            ) : null}

            {status === "success" && results.length === 0 ? (
              <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-16 text-center">
                <p className="font-display text-lg font-semibold">{t.catalog.noMatch}</p>
                <p className="mt-2 text-sm text-muted-foreground">{t.catalog.noMatchHint}</p>
                <button
                  onClick={clear}
                  className="mt-6 inline-flex h-10 items-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground"
                >
                  {t.catalog.resetFilters}
                </button>
              </div>
            ) : null}

            {status === "success" && results.length > 0 ? (
              <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {results.map((v, i) => (
                  <VehicleCard
                    key={v.id}
                    vehicle={v}
                    index={i}
                    onOpen={(vv) => {
                      setActive(vv);
                      setOpen(true);
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <VehicleModal vehicle={active} open={open} onOpenChange={setOpen} />
    </>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (s: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-lg border border-border bg-surface pl-10 pr-3 text-sm outline-none transition focus:border-primary"
      />
    </div>
  );
}

function SelectRow({
  label,
  value,
  setValue,
  options,
  allLabel,
}: {
  label: string;
  value: string;
  setValue: (v: string) => void;
  options: readonly string[];
  allLabel: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none transition focus:border-primary"
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
